import React, {
	ChangeEvent,
	KeyboardEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { AsaphButton } from '@/components/shared/Button';
import {
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import AsaphDialog from '@/components/shared/Dialog/AsaphDialog';
import { ChatInput } from '@/components/shared/Inputs';
import { SearchInput } from '@/components/shared/Inputs/SearchInput';
import { FilterChipList, FilterChipOption } from '@/components/shared/FilterChipList';
import { SendMessageButton } from '@/components/shared/SendMessageButton';
import { LinkFrom } from '@/config/constants';
import { directMessageUrl } from '@/config/urls';
import {
	Chat,
	useCreateChatMutation,
	useSendChatMessageMutation,
	useUpdateChatMutation,
} from '@/graphql';
import { FetchError } from '@/graphql/codegen-fetcher';
import { sentenceCase } from '@/helpers/changeCase';
import { useAuth } from '@/providers/AuthProvider';
import { TeamMembership, useTeam } from '@/providers/TeamProvider';
import { useSnackbar } from 'notistack';
import { uniq } from 'ramda';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { Haptics, ImpactStyle } from '@capacitor/haptics';

import { BottomActionLayout } from '../shared/BottomActionLayout/BottomActionLayout';
import { NewChatParticipantTile } from './components/NewChatParticipantTile';
import styles from './NewMessage.module.scss';

interface Member extends TeamMembership {
	isPicked: boolean;
}

interface NewMessageProps {
	handleClose: () => void;
	open: boolean;
	title?: string;
	chat?: Pick<Chat, 'userIds' | 'id'>;
	initialMessage?: boolean;
	preloadedMembers?: TeamMembership[];
}

export const NewMessage: React.FC<NewMessageProps> = ({
	handleClose,
	open,
	title = 'New message',
	chat,
	initialMessage = false,
	preloadedMembers,
}) => {
	const { user } = useAuth();
	const {
		team: { members },
	} = useTeam();
	const { mutateAsync: createDmChat, error: createDmError } =
		useCreateChatMutation<FetchError>();
	const { mutateAsync: sendMessage, error: sendMessageError } =
		useSendChatMessageMutation<FetchError>();
	const [usersList, setUsersList] = useState<Member[]>(
		parseTeamMembers(preloadedMembers || members),
	);
	const {
		mutateAsync: updateChat,
		error: updateError,
		isLoading: updateLoading,
	} = useUpdateChatMutation<FetchError>();

	const [checkedUsersList, setCheckedUsersList] = useState<Member[]>([]);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [activeFilter, setActiveFilter] = useState<string | null>(null);
	const [initialMessageValue, setInitialMessageValue] = useState<string>('');
	const handleInitialMessageChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) =>
			setInitialMessageValue(event.target.value),
		[setInitialMessageValue],
	);
	const handleEnterPress = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			if (initialMessageValue !== '') {
				createOrUpdateChatChannel();
			}
		}
	};
	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const { enqueueSnackbar } = useSnackbar();
	const hasCheckedUsers = checkedUsersList.length !== 0;
	const isUpdate = !!chat?.id;

	// const { error: manageChatError, execute: doManageChat } = useGqlMutation<
	// 	ManageChatMutation,
	// 	ManageChatMutationVariables
	// >(manageChatRegularUser);

	useEffect(() => {
		if (open) {
			setUsersList(parseTeamMembers(preloadedMembers || members));
			setCheckedUsersList([]);
			setSearchQuery('');
			setActiveFilter(null);
			setInitialMessageValue('');
		}
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (checkedUsersList.length === 0) {
			setInitialMessageValue('');
			setActiveFilter((current) =>
				current === '__selected__' ? null : current,
			);
		}
	}, [checkedUsersList]);

	useEffect(() => {
		if (createDmError != null) {
			enqueueSnackbar(createDmError, { variant: 'error' });
		}
	}, [createDmError, enqueueSnackbar]);

	const uniqueSkills = useMemo(() => {
		const skillSet = new Set<string>();
		usersList.forEach((m) => (m.user.skills ?? []).forEach((s) => skillSet.add(s)));
		return Array.from(skillSet).sort();
	}, [usersList]);

	const filteredMembers = useMemo(() => {
		return usersList.filter((member) => {
			const nameMatch = !searchQuery || (member.user.displayName ?? '').toLowerCase().includes(searchQuery.toLowerCase());
			const filterMatch =
				!activeFilter
					? true
					: activeFilter === '__selected__'
					? member.isPicked
					: (member.user.skills ?? []).includes(activeFilter) ||
					  (activeFilter === 'admin' && member.roles.includes('teamAdmin'));
			return nameMatch && filterMatch;
		});
	}, [usersList, searchQuery, activeFilter]);

	const selectedCount = checkedUsersList.length;
	const filterOptions: FilterChipOption[] = [
		...(selectedCount > 0 ? [{ label: `Selected: ${selectedCount}`, value: '__selected__' }] : []),
		{ label: 'Admin', value: 'admin' },
		...uniqueSkills.map((s) => ({ label: sentenceCase(s), value: s })),
	];

	return (
		<AsaphDialog open={open} onClose={handleClose}>
			<BottomActionLayout
				noPadding
				actionContentClassName={styles['new-message__action-bg']}
				actionContent={
					<div className={styles['new-message__action']}>
						{initialMessage ? (
							<div
								className={styles['new-message__action-input']}
							>
								<ChatInput
									id="initial-message-input"
									placeholder="Add a message"
									value={initialMessageValue}
									onKeyPress={handleEnterPress}
									onChange={handleInitialMessageChange}
								/>
								<SendMessageButton
									onClick={createOrUpdateChatChannel}
									disabled={
										!hasCheckedUsers ||
										initialMessageValue === '' ||
										isSubmitting
									}
								/>
							</div>
						) : (
							<AsaphButton
								color="blue"
								label={
									isUpdate
										? selectedCount === 1
											? `Add ${checkedUsersList[0].user.displayName ?? 'User'}`
											: selectedCount > 1
											? `Add ${selectedCount} people`
											: 'Add'
										: selectedCount === 1
										? `Message ${checkedUsersList[0].user.displayName ?? 'User'}`
										: selectedCount > 1
										? `Message ${selectedCount} people`
										: 'Message'
								}
								onClick={createOrUpdateChatChannel}
								disabled={!hasCheckedUsers}
								fullWidth
							/>
						)}
					</div>
				}
			>
				<div className={styles['new-message']}>
					<div className={styles['new-message__header']}>
						<div className={styles['new-message__toolbar']}>
							<AsaphDialogHeader
								leadingButton={{
									fadeTimeout: headerFadeTimeout,
									needsFadeIn: open,
									component: (
										<AsaphDialogHeaderButton
											type="text"
											label="Cancel"
											handlePress={
												cancelCreatingNewMessage
											}
										/>
									),
								}}
								title={{
									fadeTimeout: headerFadeTimeout,
									needsFadeIn: open,
									label: title,
								}}
								trailingButton={{
									fadeTimeout: headerFadeTimeout,
									needsFadeIn: open,
									component: null,
								}}
							/>
						</div>
						<div className={styles['new-message__search']}>
							<SearchInput
								id="new-message-search"
								name="new-message-search"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								clearSearchQuery={() => setSearchQuery('')}
								placeholder="Search people"
								fullWidth
							/>
						</div>
						<div className={styles['new-message__chips']}>
							<FilterChipList
								options={filterOptions}
								value={activeFilter}
								onChange={setActiveFilter}
								className={styles['new-message__chip-list']}
							/>
						</div>
						</div>
					<div className={styles['new-message__panel']}>
						<div className={styles['new-message__list']}>
							{filteredMembers.map((member, index) => (
								<React.Fragment key={member.user.id}>
									<NewChatParticipantTile
										userId={member.user.id}
										name={member.user.displayName || 'Unknown'}
										isAdmin={member.roles.includes('teamAdmin')}
										checked={member.isPicked}
										handlePress={() => onNewChatParticipantTilePress(member.user.id)}
									/>
									{index < filteredMembers.length - 1 && (
										<div className={styles['new-message__divider-wrapper']}>
											<div className={styles['new-message__divider']} />
										</div>
									)}
								</React.Fragment>
							))}
						</div>
					</div>
				</div>
			</BottomActionLayout>
		</AsaphDialog>
	);

	function onNewChatParticipantTilePress(id: string) {
		Haptics.impact({ style: ImpactStyle.Medium });
		setUsersList(
			usersList.map((member) => {
				if (id === member.user.id) {
					const updatedUser = {
						...member,
						isPicked: !member.isPicked,
					};
					if (member.isPicked) {
						let updatedCheckedUsersList: Member[] = [];
						checkedUsersList.forEach((member) => {
							if (member.user.id !== id) {
								updatedCheckedUsersList.push(member);
							}
						});
						setCheckedUsersList(updatedCheckedUsersList);
					} else {
						const updatedCheckedUsersList = [
							...checkedUsersList,
							updatedUser,
						];
						setCheckedUsersList(updatedCheckedUsersList);
					}
					return updatedUser;
				}
				return member;
			}),
		);
	}

	async function createOrUpdateChatChannel() {
		if (checkedUsersList.length) {
			const userIds = checkedUsersList.map((u) => u.user.id);
			// Include own user id
			if (!userIds.includes(user.id)) {
				userIds.push(user.id);
			}

			setIsSubmitting(true);
			try {
				if (isUpdate) {
					await updateChat({
						chatId: chat.id,
						userIds: uniq([...chat.userIds, ...userIds]),
					});
					setIsSubmitting(false);
					cancelCreatingNewMessage();
					return;
				}

				const result = await createDmChat({
					userIds,
				});

				const newChatId = result.upsertChat.id;

				if (initialMessage) {
					try {
						await sendMessage({
							chatId: newChatId,
							input: {
								id: uuidv4(),
								content: initialMessageValue,
							},
						});
					} catch (e) {
						let errorMessage =
							'Unknown error, please try again later';
						if (e instanceof FetchError) {
							errorMessage = e.message;
						}
						setIsSubmitting(false);
						enqueueSnackbar(errorMessage, {
							variant: 'error',
						});
					}
				}

				setIsSubmitting(false);
				cancelCreatingNewMessage();

				// Redirect the user to the newly created channel
				navigate(directMessageUrl.replace(':token', newChatId), {
					state: {
						linkFrom: LinkFrom.messages,
					},
				});
			} catch (e) {
				let errorMessage = 'Unknown error, please try again later';
				if (e instanceof FetchError) {
					errorMessage = e.message;
				}
				setIsSubmitting(false);
				enqueueSnackbar(errorMessage, {
					variant: 'error',
				});
			}
		}
	}

	function cancelCreatingNewMessage() {
		handleClose();
		setCheckedUsersList([]);
		setUsersList(parseTeamMembers(preloadedMembers || members));
	}

	function parseTeamMembers(memberships: TeamMembership[]): Member[] {
		return memberships
			.filter((membership) => membership.user?.id !== user.id)
			.map((membership) => {
				return {
					...membership,
					isPicked: false,
				};
			})
			.sort((membershipA, membershipB) => {
				const textA =
					membershipA.user?.displayName?.toUpperCase() || 'Unknown';
				const textB =
					membershipB.user?.displayName?.toUpperCase() || 'Unknown';
				return textA < textB ? -1 : textA > textB ? 1 : 0;
			});
	}
};

const headerFadeTimeout = 300;
